import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Close, Redeem } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import commonMessages from '@/features/i18n/commonMessages';
import { durandalRoutes } from '@/routes/durandal';

const messages = defineMessages({
  TitleNotReferred: {
    defaultMessage: 'Refer a business, earn rewards!',
  },
  TitleReferred: {
    defaultMessage: 'Refer a business and keep on earning!',
  },
  MessageNotReferred: {
    defaultMessage:
      'Share Plooto and enjoy 4 FREE months of subscription for every successful referral.',
  },
  MessageReferred: {
    defaultMessage:
      "We're happy you were referred to try Plooto! Now, enjoy 4 FREE months of subscription for every successful referral you make.",
  },
  SeeHowItWorks: {
    defaultMessage: 'See How It Works',
  },
  CloseReferralsBanner: {
    defaultMessage: 'Close referrals banner',
  },
});

export type ReferralsBannerProps = {
  variant: 'not-referred' | 'referred';
  onDismiss: () => void;
  onNavigateToReferrals: () => void;
};

export const ReferralsBanner = ({
  variant,
  onDismiss,
  onNavigateToReferrals,
}: ReferralsBannerProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.only('xs'));
  return (
    <Card
      variant="outlined"
      sx={{
        // We customize these props here to blend in with the existing dashboard sidebar widgets. This
        // should be removed when the dashboard redesign is implemented in favour of the styles
        // applied by variant="outlined".
        border: 'solid 1px #dae1e9',
        boxShadow: '0 1px 1px 0 #dae1e9',
        // These are flavour customizations for this specific component, and should not be removed
        // with the dashboard redesign.
        borderLeftColor: 'success.main',
        borderLeftWidth: 4,
      }}
    >
      <Stack
        p={2}
        gap={{ xs: 1, sm: 2 }}
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Redeem color="success" fontSize="large" />
        <Box flexGrow="1">
          {variant === 'referred' ? (
            <>
              <Typography variant="h4" component="div">
                <FormattedMessage {...messages.TitleReferred} />
              </Typography>
              <Typography variant="body1" component="div">
                <FormattedMessage {...messages.MessageReferred} />
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h4" component="div">
                <FormattedMessage {...messages.TitleNotReferred} />
              </Typography>
              <Typography variant="body1" component="div">
                <FormattedMessage {...messages.MessageNotReferred} />
              </Typography>
            </>
          )}
        </Box>
        <Stack gap={1} direction="row" alignItems="center" textAlign="center">
          <Button
            variant={xs ? 'outlined' : 'text'}
            color="primary"
            href={durandalRoutes.user.referrals}
            onClick={onNavigateToReferrals}
            fullWidth
            sx={{ textAlign: 'center' }}
          >
            <FormattedMessage {...messages.SeeHowItWorks} />
          </Button>
          {xs ? (
            <Button variant="outlined" color="secondary" onClick={onDismiss} fullWidth>
              <FormattedMessage {...commonMessages.close} />
            </Button>
          ) : (
            <IconButton
              color="inherit"
              onClick={onDismiss}
              title={formatMessage(messages.CloseReferralsBanner)}
            >
              <Close />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
